<template>
      <div class="questionario-content">
        <transition-group name="toast" tag="div" class="toast-stack">
          <div
            v-for="toast in toasts"
            :key="toast.id"
            class="toast"
            :class="`toast--${toast.type}`"
          >
            <span class="toast-message">{{ toast.message }}</span>
            <button type="button" class="toast-close" @click="dismissToast(toast.id)">×</button>
          </div>
        </transition-group>
        <div class="card questionario-card" ref="printableCard">
          <header class="card-header">
            <div class="card-header__title">
              <h1>Check Finale</h1>
            </div>
            <div class="card-header__right">
              <div class="meta">
                <span v-if="questionarioInfo.updatedAt" class="muted">
                  Ultimo aggiornamento: {{ formatDate(questionarioInfo.updatedAt) }}
                </span>
                <span v-else class="muted">Nessun dato salvato</span>
              </div>
              <div class="card-header__actions">
                <button
                  type="button"
                  class="ghost-button"
                  @click="copyQuestionarioAnswers"
                  :disabled="copyingAnswers"
                  :aria-busy="copyingAnswers"
                  title="Copia check finale"
                  aria-label="Copia check finale"
                >
                  <span v-if="copyingAnswers" class="ghost-button__spinner" aria-hidden="true"></span>
                  <span v-else class="material-icons" aria-hidden="true" style="color:#fff">file_copy</span>
                </button>
                <button
                  type="button"
                  class="ghost-button"
                  @click="pasteQuestionarioAnswers"
                  :disabled="pastingAnswers"
                  :aria-busy="pastingAnswers"
                  title="Incolla check finale"
                  aria-label="Incolla check finale"
                >
                  <span v-if="pastingAnswers" class="ghost-button__spinner" aria-hidden="true"></span>
                  <span v-else class="material-icons" aria-hidden="true" style="color:#fff">content_paste</span>
                </button>
                <button
                  type="button"
                  class="ghost-button"
                  @click="exportFormAsPdf"
                  :disabled="pdfExporting"
                  title="Esporta PDF"
                  aria-label="Esporta PDF"
                  :aria-busy="pdfExporting"
                >
                  <span v-if="pdfExporting" class="ghost-button__spinner" aria-hidden="true"></span>
                  <span v-else class="material-icons" aria-hidden="true" style="color:#fff">picture_as_pdf</span>
                </button>
                <button
                  type="button"
                  class="ghost-button"
                  @click="copyFormLink"
                  title="Copia link form"
                  aria-label="Copia link form"
                >
                  <span class="material-icons" aria-hidden="true" style="color:#fff">content_copy</span>
                </button>
                <button
                  type="button"
                  class="ghost-button"
                  @click="togglePartialCheck"
                  :disabled="partialSaving"
                  :title="questionarioPartial ? 'Rendi completo' : 'Rendi parziale'"
                  :aria-label="questionarioPartial ? 'Rendi completo' : 'Rendi parziale'"
                >
                  <span class="material-icons" aria-hidden="true" style="color:#fff">
                    {{ partialSaving ? 'hourglass_top' : questionarioPartial ? 'rule' : 'rule_folder' }}
                  </span>
                </button>
              </div>
            </div>
          </header>
          <p
            v-if="questionarioPartial"
            class="muted"
            style="margin: 0 0 12px;"
          >
            Modalità parziale attiva: foto e misure non sono obbligatorie per l’utente.
          </p>
          <div v-if="canManageAdmin" class="admin-wrapper">
            <div class="admin-controls">
              <div class="admin-controls__status">
                <span class="admin-chip" :class="adminChipClass">
                  {{ adminStatusLabel }}
                </span>
                <span v-if="adminState.updatedAt" class="admin-status-note">
                  Aggiornato: {{ formatDate(adminState.updatedAt) }}
                </span>
                <span v-if="adminLoading" class="admin-status-note">Aggiornamento…</span>
              </div>
              <div class="admin-controls__actions">
                <button
                  type="button"
                  class="admin-btn admin-btn--green"
                  @click="handleAdminApprove"
                  :disabled="adminSaving || adminLoading"
                >
                  Approva
                </button>
                <button
                  type="button"
                  class="admin-btn admin-btn--orange"
                  @click="toggleNeedsUpdate"
                  :disabled="adminSaving"
                >
                  Richiedi
                </button>
              </div>
            </div>
            <div v-if="needsUpdateOpen" class="admin-needs-box">
              <input
                v-model.trim="needsUpdateNote"
                class="admin-note-input"
                type="text"
                placeholder="Scrivi la nota per l'utente…"
              />
              <div class="admin-controls__actions">
                <button
                  type="button"
                  class="admin-btn admin-btn--green staff-button-positive"
                  @click="saveNeedsUpdateRequest"
                  :disabled="adminSaving"
                >
                  Salva
                </button>
                <button type="button" class="admin-btn admin-btn--grey" @click="toggleNeedsUpdate">
                  Chiudi
                </button>
              </div>
            </div>
            <p v-else-if="adminState.note" class="admin-note">Nota: “{{ adminState.note }}”</p>
          </div>
          <p v-else class="admin-helper muted">
            Collega questo check finale a un programma per gestire lo stato admin.
          </p>
          <p v-if="adminError" class="admin-error">{{ adminError }}</p>
          <div v-if="loading" class="state state-loading">Caricamento check finale…</div>
          <div v-else-if="errorMessage" class="state state-error">{{ errorMessage }}</div>
          <template v-else>
            <form class="qi-form" @submit.prevent="saveQuestionario">
              <!-- Allenamento -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Allenamento</h2>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>Quante sedute hai svolto in media a settimana?</span>
                      <select v-model="form.seduteSettimanali">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in SEDUTE_SETTIMANALI_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Quanto hai rispettato l'allenamento da 1 a 100?</span>
                      <select v-model="form.adesioneAllenamento">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in ADESIONE_PERCENT_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                  </div>
                  <label>
                    <span>Hai affiancato altre attività “sportive” ai nostri allenamenti? Scrivici qui quali e quante volte a settimana…</span>
                    <textarea v-model.trim="form.attivitaAffiancate" rows="3"></textarea>
                  </label>
                  <label>
                    <span>Scrivi qui eventuali note riguardanti l’allenamento, più info ci dai e più potremo darti supporto…</span>
                    <textarea v-model.trim="form.noteAllenamento" rows="3"></textarea>
                  </label>
                </div>
              </section>

              <!-- Alimentazione -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Alimentazione</h2>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>Hai rispettato la strategia alimentare (ciclizzazione dei carboidrati)?</span>
                      <select v-model="form.strategiaAlimentare">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in STRATEGIA_ALIMENTARE_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Hai rispettato le grammature assegnate?</span>
                      <select v-model="form.rispettoGrammature">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in RISPETTO_GRAMMATURE_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                  </div>

                  <div class="grid two">
                    <label>
                      <span>Quanti pasti liberi hai fatto in questo programma?</span>
                      <select v-model="form.pastiLiberi">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in PASTI_LIBERI_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Media giornaliera di acqua assunta?</span>
                      <select v-model="form.acquaMedia">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in ACQUA_MEDIA_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                  </div>
                  <label>
                    <span>Scrivi qui eventuali note riguardanti l’alimentazione, più info ci dai e più potremo darti supporto…</span>
                    <textarea v-model.trim="form.noteAlimentazione" rows="3"></textarea>
                  </label>
                  <label>
                    <span>Quanto hai rispettato la dieta da 1 a 100?</span>
                    <select v-model="form.adesioneAlimentazione">
                      <option value="">Seleziona…</option>
                      <option v-for="opt in ADESIONE_PERCENT_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                    </select>
                  </label>
                </div>
              </section>

              <!-- Recupero -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Recupero</h2>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>Quante ore stai dormendo in media?</span>
                      <select v-model="form.recuperoOreSonno">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in ORE_SONNO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Il sonno è continuativo?</span>
                      <select v-model="form.recuperoSonnoContinuativo">
                        <option value="">Seleziona…</option>
                        <option value="Si">Si</option>
                        <option value="No">No</option>
                      </select>
                    </label>
                  </div>
                  <label v-if="form.recuperoSonnoContinuativo === 'No'">
                    <span>Se no, spiegaci il motivo</span>
                    <textarea v-model.trim="form.recuperoSonnoMotivo" rows="2"></textarea>
                  </label>
                  <div class="grid two">
                    <label>
                      <span>Come va il sonno rispetto al mese precedente?</span>
                      <select v-model="form.recuperoSonnoTrend">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in TREND_SONNO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Stai vivendo un periodo stressante?</span>
                      <select v-model="form.recuperoStress">
                        <option value="">Seleziona…</option>
                        <option value="Si">Si</option>
                        <option value="No">No</option>
                      </select>
                    </label>
                  </div>
                </div>
              </section>

              <!-- Integrazione -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Integrazione</h2>
                </header>
                <div class="qi-block__body">
                  <label>
                    <span>Hai assunto integratori alimentari?</span>
                    <select v-model="form.integrazioneAssunta">
                      <option value="">Seleziona…</option>
                      <option value="Si">Si</option>
                      <option value="No">No</option>
                    </select>
                  </label>
                  <label v-if="form.integrazioneAssunta === 'Si'">
                    <span>Se sì, scrivili qui…</span>
                    <textarea v-model.trim="form.integrazioneDettagli" rows="2"></textarea>
                  </label>
                </div>
              </section>

              <!-- Misurazioni -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <div class="qi-block__header-text">
                    <h2 class="qi-block__title">Misurazioni</h2>
                    <p class="qi-block__subtitle muted">
                      Circonferenza addome: 1 cm sopra l'ombelico • Coscia: metà tra anca e ginocchio • Fianchi: punto più largo dell'esterno coscia.
                    </p>
                  </div>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>Misure prese in preciclo/ciclo?</span>
                      <select v-model="form.misurePreciclo">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>Peso (kg)</span>
                      <input v-model.trim="form.peso" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Altezza (cm)</span>
                      <input v-model.trim="form.altezza" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Fianchi (cm)</span>
                      <input v-model.trim="form.fianchi" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Addome (cm)</span>
                      <input v-model.trim="form.addome" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Torace (cm)</span>
                      <input v-model.trim="form.torace" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Coscia (cm)</span>
                      <input v-model.trim="form.coscia" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Braccio (cm)</span>
                      <input v-model.trim="form.braccio" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Massa magra (%)</span>
                      <input v-model.trim="form.massaMagra" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Massa grassa (%)</span>
                      <input v-model.trim="form.massaGrassa" type="text" inputmode="decimal" />
                    </label>
                    <label>
                      <span>Acqua corporea (%)</span>
                      <input v-model.trim="form.acquaCorporea" type="text" inputmode="decimal" />
                    </label>
                  </div>
                  <label>
                    <span>Note sulle misurazioni</span>
                    <textarea v-model.trim="form.misurazioniNote" rows="3"></textarea>
                  </label>
                </div>
              </section>

              <!-- Foto -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <div class="qi-block__header-text">
                    <h2 class="qi-block__title">Foto</h2>
                    <p class="photo-intro">Carica foto a figura intera, con buona luce e qualità.</p>
                  </div>
                </header>
                <div class="qi-block__body">
                  <div class="photo-grid">
                    <div class="photo-card">
                      <h3 class="photo-card__title">Foto frontale</h3>
                      <p class="photo-card__description">Braccia lungo i fianchi, gambe leggermente divaricate.</p>
                      <div class="photo-card__upload">
                        <span class="photo-card__upload-label">Carica file</span>
                        <input type="file" accept="image/*" @change="onPhotoChange('anteriore', $event)" />
                      </div>
                      <div class="photo-card__status">
                        <div v-if="uploadingPhoto.anteriore" class="upload-progress">
                          <span class="spinner"></span>
                          <span>Caricamento…</span>
                        </div>
                        <div v-else-if="form.foto.anteriore" class="file-meta">
                          <div v-if="isImageFile(form.foto.anteriore)" class="file-preview">
                            <img
                              :src="form.foto.anteriore.url"
                              alt="Anteprima foto frontale"
                              loading="lazy"
                            />
                          </div>
                          <a :href="form.foto.anteriore.url" target="_blank" rel="noopener">{{ form.foto.anteriore.name }}</a>
                          <button class="staff-button-danger" type="button" @click="removePhoto('anteriore')">Elimina</button>
                        </div>
                      </div>
                    </div>
                    <div class="photo-card">
                      <h3 class="photo-card__title">Foto posteriore</h3>
                      <p class="photo-card__description">Braccia lungo i fianchi, capelli legati o in avanti.</p>
                      <div class="photo-card__upload">
                        <span class="photo-card__upload-label">Carica file</span>
                        <input type="file" accept="image/*" @change="onPhotoChange('posteriore', $event)" />
                      </div>
                      <div class="photo-card__status">
                        <div v-if="uploadingPhoto.posteriore" class="upload-progress">
                          <span class="spinner"></span>
                          <span>Caricamento…</span>
                        </div>
                        <div v-else-if="form.foto.posteriore" class="file-meta">
                          <div v-if="isImageFile(form.foto.posteriore)" class="file-preview">
                            <img
                              :src="form.foto.posteriore.url"
                              alt="Anteprima foto posteriore"
                              loading="lazy"
                            />
                          </div>
                          <a :href="form.foto.posteriore.url" target="_blank" rel="noopener">{{ form.foto.posteriore.name }}</a>
                          <button class="staff-button-danger" type="button" @click="removePhoto('posteriore')">Elimina</button>
                        </div>
                      </div>
                    </div>
                    <div class="photo-card">
                      <h3 class="photo-card__title">Foto profilo sinistro</h3>
                      <p class="photo-card__description">Braccia avanti e tese, capelli raccolti.</p>
                      <div class="photo-card__upload">
                        <span class="photo-card__upload-label">Carica file</span>
                        <input type="file" accept="image/*" @change="onPhotoChange('profiloSinistra', $event)" />
                      </div>
                      <div class="photo-card__status">
                        <div v-if="uploadingPhoto.profiloSinistra" class="upload-progress">
                          <span class="spinner"></span>
                          <span>Caricamento…</span>
                        </div>
                        <div v-else-if="form.foto.profiloSinistra" class="file-meta">
                          <div v-if="isImageFile(form.foto.profiloSinistra)" class="file-preview">
                            <img
                              :src="form.foto.profiloSinistra.url"
                              alt="Anteprima foto profilo sinistro"
                              loading="lazy"
                            />
                          </div>
                          <a :href="form.foto.profiloSinistra.url" target="_blank" rel="noopener">{{ form.foto.profiloSinistra.name }}</a>
                          <button class="staff-button-danger" type="button" @click="removePhoto('profiloSinistra')">Elimina</button>
                        </div>
                      </div>
                    </div>
                    <div class="photo-card">
                      <h3 class="photo-card__title">Foto profilo destro</h3>
                      <p class="photo-card__description">Braccia avanti e tese, capelli raccolti.</p>
                      <div class="photo-card__upload">
                        <span class="photo-card__upload-label">Carica file</span>
                        <input type="file" accept="image/*" @change="onPhotoChange('profiloDestra', $event)" />
                      </div>
                      <div class="photo-card__status">
                        <div v-if="uploadingPhoto.profiloDestra" class="upload-progress">
                          <span class="spinner"></span>
                          <span>Caricamento…</span>
                        </div>
                        <div v-else-if="form.foto.profiloDestra" class="file-meta">
                          <div v-if="isImageFile(form.foto.profiloDestra)" class="file-preview">
                            <img
                              :src="form.foto.profiloDestra.url"
                              alt="Anteprima foto profilo destro"
                              loading="lazy"
                            />
                          </div>
                          <a :href="form.foto.profiloDestra.url" target="_blank" rel="noopener">{{ form.foto.profiloDestra.name }}</a>
                          <button class="staff-button-danger" type="button" @click="removePhoto('profiloDestra')">Elimina</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Note generali -->
              <section class="qi-block">
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Note generali</h2>
                </header>
                <div class="qi-block__body">
                  <label>
                    <span>Scrivi qui ogni comunicazione necessaria a valutare questo programma, includendo le informazioni più importanti condivise con lo staff.</span>
                    <textarea v-model.trim="form.noteGenerali" rows="4"></textarea>
                  </label>
                </div>
              </section>

              <footer class="form-footer">
                <div class="left">
                  <button type="submit" class="primary-button staff-button-positive" :disabled="saving || loading">
                    {{ saving ? 'Salvataggio…' : 'Crea bozza' }}
                  </button>
                  <button
                    type="button"
                    class="secondary-button staff-button-cancel"
                    :disabled="saving || loading || !dirty"
                    @click="resetForm"
                  >
                    Annulla modifiche
                  </button>
                  <button
                    type="button"
                    class="primary-button primary-button--success"
                    :disabled="saving || loading || completingStep"
                    @click="completeCheckStep"
                  >
                    {{ completingStep ? 'Segna…' : 'Metti in lavorazione' }}
                  </button>
                </div>
                <div class="right">
                  <span v-if="dirty" class="dirty-indicator">Modifiche non salvate</span>
                </div>
              </footer>
            </form>

          </template>
        </div>
      </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
} from 'firebase/storage';
import { getAuth, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';

import {
  flattenAnswers,
  groupEntries,
  prettifyKey,
  sanitizeQuestionarioAnswers,
} from '../../lib/questionarioDisplay.js';
import { dispatchNeedsUpdateNotification } from '../../lib/percorsoStepNotifications';
import { buildQuestionarioPdfFileName } from '../../lib/pdfExport';
import { generateCheckFinalePdf } from '@/lib/programmiPdf';
import { setStepDone } from '@/lib/percorsi';

const QUESTIONARI_COLLECTION = 'questionari';
const QUESTIONARIO_TYPE = 'user_programmi_check_finale';
const QUESTIONARIO_TEMPLATE = {
  id: 'user_programmi_check_finale_v1',
  key: 'user_programmi_check_finale',
  label: 'Check Finale Programmi',
  version: 1,
};

const SEDUTE_SETTIMANALI_OPTIONS = ['1', '2', '3', '4', '5', '5+'];
const ADESIONE_PERCENT_OPTIONS = ['10', '20', '30', '40', '50', '60', '70', '80', '90', '100'];
const STRATEGIA_ALIMENTARE_OPTIONS = ['Si', 'No', 'A volte'];
const RISPETTO_GRAMMATURE_OPTIONS = ['Si', 'No, ne ho mangiate meno', 'No, ne ho mangiate di più'];
const PASTI_LIBERI_OPTIONS = ['4', 'Meno di 4', 'Più di 4'];
const ACQUA_MEDIA_OPTIONS = ['1', '2', '3', '4'];
const ORE_SONNO_OPTIONS = ['Meno di 5', '6', '7', '8', '9', 'Più di 9'];
const TREND_SONNO_OPTIONS = ['Meglio', 'Peggio', 'Stabile'];
const YES_NO_OPTIONS = ['Si', 'No'];

const QUESTIONARIO_INITIAL_TYPE = 'user_programmi_questionario';

const deepClone = (value) => JSON.parse(JSON.stringify(value ?? null));
const INVALID_STORAGE_CHARS = /[\\/?%*:|"<>#[\]]/g;
const sanitizeName = (name = '') => {
  const cleaned = name.replace(INVALID_STORAGE_CHARS, '').replace(/\s+/g, '_');
  let result = '';
  for (const ch of cleaned) {
    if (ch.charCodeAt(0) >= 32) result += ch;
  }
  return result;
};

const PORTAL_BASE_URL = 'https://www.metodofusco.it/tickets';

const toDateSafe = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const formatDate = (value) => {
  const date = toDateSafe(value);
  if (!date) return '';
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const toIsoString = (value) => {
  const date = toDateSafe(value);
  return date ? date.toISOString() : null;
};

const defaultForm = () => ({
  seduteSettimanali: '',
  attivitaAffiancate: '',
  noteAllenamento: '',
  adesioneAllenamento: '',
  strategiaAlimentare: '',
  rispettoGrammature: '',
  pastiLiberi: '',
  acquaMedia: '',
  noteAlimentazione: '',
  adesioneAlimentazione: '',
  recuperoOreSonno: '',
  recuperoSonnoContinuativo: '',
  recuperoSonnoMotivo: '',
  recuperoSonnoTrend: '',
  recuperoStress: '',
  integrazioneAssunta: '',
  integrazioneDettagli: '',
  misurePreciclo: '',
  peso: '',
  altezza: '',
  fianchi: '',
  addome: '',
  torace: '',
  coscia: '',
  braccio: '',
  massaMagra: '',
  massaGrassa: '',
  acquaCorporea: '',
  misurazioniNote: '',
  foto: {
    posteriore: null,
    anteriore: null,
    profiloDestra: null,
    profiloSinistra: null,
  },
  noteGenerali: '',
});

const pickQueryValue = (key, route) => {
  const raw = route.query?.[key];
  if (Array.isArray(raw)) return raw[0] ?? null;
  return raw ?? null;
};

const toTrimmedStringOrNull = (value) => {
  if (value === undefined || value === null) return null;
  const str = String(value);
  const trimmed = str.trim();
  return trimmed ? trimmed : null;
};

const toStepIndex = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  const intVal = Math.round(num);
  return intVal >= 0 ? intVal : null;
};

export default {
  name: 'UserProgrammiCheckFinale',
  props: {
    userId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const route = useRoute();
    const router = useRouter();
    const db = getFirestore();
    const storage = getStorage();
    const auth = getAuth();

    const form = reactive(defaultForm());
    const printableCard = ref(null);
    const lastLoaded = ref(deepClone(form));
    const questionarioDocId = ref('');
    const questionarioInfo = ref({ createdAt: null, updatedAt: null, templateLabel: QUESTIONARIO_TEMPLATE.label });
    const questionarioPartial = ref(false);
    const partialSaving = ref(false);
    const pdfExporting = ref(false);
    const copyingAnswers = ref(false);
    const pastingAnswers = ref(false);
    const userDisplayName = computed(() => {
      const name = toTrimmedStringOrNull(pickQueryValue('userName', route));
      const surname = toTrimmedStringOrNull(pickQueryValue('userSurname', route));
      return [name, surname].filter(Boolean).join(' ').trim();
    });
    const adminState = reactive({
      status: 'none',
      note: '',
      updatedAt: null,
    });
    const adminLoading = ref(false);
    const adminSaving = ref(false);
    const adminError = ref('');
    const needsUpdateOpen = ref(false);
    const needsUpdateNote = ref('');
    let adminStateRequestId = 0;
    const suppressDirty = ref(false);

    const loading = ref(true);
    const saving = ref(false);
    const completingStep = ref(false);
    const dirty = ref(false);
    const errorMessage = ref('');
    const toasts = ref([]);
    const toastTimers = new Map();

    const currentUserId = ref(
      props.userId ||
      route.params.userId ||
      toTrimmedStringOrNull(pickQueryValue('userId', route)) ||
      ''
    );
    const effectiveUserId = computed(() => currentUserId.value || auth.currentUser?.uid || '');

    const uploadingPhoto = reactive({});

    const setUploadState = (store, key, value) => {
      store[key] = value;
    };

    const isImageFile = (metadata) => {
      if (!metadata) return false;
      const type = String(metadata.contentType || '').toLowerCase();
      if (type.startsWith('image/')) return true;
      const name = String(metadata.name || '').toLowerCase();
      return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name);
    };

    const percorsoContext = computed(() => {
      const stepIndex = toStepIndex(pickQueryValue('stepIndex', route));
      const sequenceIndex = toStepIndex(pickQueryValue('sequenceIndex', route));
      const sequenceTotal = toStepIndex(pickQueryValue('sequenceTotal', route));
      return {
        percorsoId: toTrimmedStringOrNull(pickQueryValue('percorsoId', route)),
        stepIndex,
        stepKey: toTrimmedStringOrNull(pickQueryValue('stepKey', route)) || 'check_finale',
        sequenceGroupId: toTrimmedStringOrNull(pickQueryValue('sequenceGroupId', route)),
        sequenceIndex,
        sequenceTotal,
        productId: toTrimmedStringOrNull(pickQueryValue('productId', route)),
        productName: toTrimmedStringOrNull(pickQueryValue('productName', route)),
        programTitle: toTrimmedStringOrNull(pickQueryValue('programTitle', route)),
        programLabel: toTrimmedStringOrNull(pickQueryValue('programLabel', route)),
        plannedStartAt: toTrimmedStringOrNull(pickQueryValue('plannedStartAt', route)),
        expireAt: toTrimmedStringOrNull(pickQueryValue('expireAt', route)),
        stepName: toTrimmedStringOrNull(pickQueryValue('stepName', route)),
        orderRef: toTrimmedStringOrNull(pickQueryValue('orderRef', route)),
      };
    });

    const manualDocLinkState = reactive({
      shouldDetach: false,
      docId: '',
      snapshot: null,
    });
    const customToken = computed(() => toTrimmedStringOrNull(pickQueryValue('customToken', route)));
    const manualStandaloneView = computed(() => {
      const raw = pickQueryValue('manualStandalone', route);
      if (raw == null) return false;
      const value = String(raw).trim().toLowerCase();
      return value === '1' || value === 'true';
    });
    const resetManualDocLinkState = () => {
      manualDocLinkState.shouldDetach = false;
      manualDocLinkState.docId = '';
      manualDocLinkState.snapshot = null;
    };
    const evaluateManualDocLinkState = (docId, data) => {
      const ctx = percorsoContext.value;
      const hasPercorsoContext = !!ctx.percorsoId && Number.isInteger(ctx.stepIndex);
      if (!manualStandaloneView.value && hasPercorsoContext && data?.manualStandalone) {
        manualDocLinkState.shouldDetach = true;
        manualDocLinkState.docId = docId;
        manualDocLinkState.snapshot = data;
      } else {
        resetManualDocLinkState();
      }
    };

    const questionarioTargetKey = computed(() => {
      const ctx = percorsoContext.value;
      return [
        effectiveUserId.value || '',
        ctx.percorsoId || '',
        Number.isInteger(ctx.stepIndex) ? String(ctx.stepIndex) : 'x',
        ctx.stepKey || '',
      ].join('__');
    });
    let authUnsubscribe = null;

    const canManageAdmin = computed(() => {
      const ctx = percorsoContext.value;
      return !!ctx.percorsoId && Number.isInteger(ctx.stepIndex);
    });

    const adminStatusLabel = computed(() => {
      if (adminState.status === 'approved') return 'Approvato';
      if (adminState.status === 'needs_update') return 'Richiesto aggiornamento';
      return '—';
    });

    const adminChipClass = computed(() => {
      if (adminState.status === 'approved') return 'chip-green';
      if (adminState.status === 'needs_update') return 'chip-orange';
      return 'chip-neutral';
    });

    const dismissToast = (id) => {
      toasts.value = toasts.value.filter((toast) => toast.id !== id);
      const timer = toastTimers.get(id);
      if (timer) {
        clearTimeout(timer);
        toastTimers.delete(id);
      }
    };

    const pushToast = (message, type = 'info') => {
      if (!message) return;
      const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      toasts.value.push({ id, message, type });
      const timer = setTimeout(() => dismissToast(id), 4200);
      toastTimers.set(id, timer);
    };

    const showActionMessage = (message, type = 'success') => {
      if (!message) return;
      pushToast(message, type);
    };

    const assignAdminState = (step = {}) => {
      adminState.status = step?.adminStatus || 'none';
      adminState.note = step?.adminNote || '';
      adminState.updatedAt = step?.adminUpdatedAt ? toDateSafe(step.adminUpdatedAt) : null;
    };

    const refreshAdminState = async () => {
      const ctx = percorsoContext.value;
      const idx = Number.isInteger(ctx.stepIndex) ? ctx.stepIndex : null;
      needsUpdateOpen.value = false;
      if (!ctx.percorsoId || idx == null) {
        assignAdminState({});
        needsUpdateNote.value = '';
        adminError.value = '';
        adminLoading.value = false;
        return;
      }

      const requestId = ++adminStateRequestId;
      adminLoading.value = true;
      adminError.value = '';
      try {
        const percorsoRef = doc(db, 'percorsi', ctx.percorsoId);
        const snap = await getDoc(percorsoRef);
        if (!snap.exists()) {
          if (requestId === adminStateRequestId) {
            assignAdminState({});
            adminError.value = 'Percorso non trovato.';
          }
          return;
        }
        const steps = Array.isArray(snap.data()?.steps) ? snap.data().steps : [];
        const step = steps[idx] || {};
        if (requestId === adminStateRequestId) {
          assignAdminState(step);
          needsUpdateNote.value = step.adminNote || '';
        }
      } catch (error) {
        console.error('Errore caricando lo stato admin del check finale:', error);
        if (requestId === adminStateRequestId) {
          adminError.value = 'Impossibile recuperare lo stato admin.';
        }
      } finally {
        if (requestId === adminStateRequestId) {
          adminLoading.value = false;
        }
      }
    };

    const mutatePercorsoStep = async (mutator) => {
      const ctx = percorsoContext.value;
      const idx = Number.isInteger(ctx.stepIndex) ? ctx.stepIndex : null;
      if (!ctx.percorsoId || idx == null) {
        throw new Error('Step non collegato a un percorso valido.');
      }
      const percorsoRef = doc(db, 'percorsi', ctx.percorsoId);
      const snap = await getDoc(percorsoRef);
      if (!snap.exists()) {
        throw new Error('Percorso non trovato.');
      }
      const data = snap.data() || {};
      const steps = Array.isArray(data.steps) ? [...data.steps] : [];
      const prev = steps[idx] ? { ...steps[idx] } : {};
      const next = mutator({ ...prev }) || prev;
      steps[idx] = next;
      await updateDoc(percorsoRef, { steps });
      return next;
    };

    const handleAdminApprove = async () => {
      if (!canManageAdmin.value) {
        showActionMessage('Collega il check finale a un percorso per aggiornare lo stato admin.', 'error');
        return;
      }
      adminSaving.value = true;
      try {
        const nextStep = await mutatePercorsoStep((prev) => ({
          ...prev,
          adminStatus: 'approved',
          adminNote: null,
          adminUpdatedAt: new Date().toISOString(),
        }));
        assignAdminState(nextStep);
        needsUpdateOpen.value = false;
        needsUpdateNote.value = '';
        showActionMessage('Check finale approvato.');
      } catch (error) {
        console.error('setAdminApproved error:', error);
        showActionMessage('Errore nel salvataggio dello stato admin.', 'error');
      } finally {
        adminSaving.value = false;
      }
    };

    const toggleNeedsUpdate = () => {
      if (!canManageAdmin.value) {
        showActionMessage('Collega il check finale a un percorso per richiedere aggiornamenti.', 'error');
        return;
      }
      needsUpdateOpen.value = !needsUpdateOpen.value;
      if (needsUpdateOpen.value) {
        needsUpdateNote.value = adminState.note || '';
      }
    };

    const saveNeedsUpdateRequest = async () => {
      if (!canManageAdmin.value) {
        showActionMessage('Collega il check finale a un percorso per richiedere aggiornamenti.', 'error');
        return;
      }
      const note = (needsUpdateNote.value || '').trim();
      adminSaving.value = true;
      try {
        const nextStep = await mutatePercorsoStep((prev) => ({
          ...prev,
          adminStatus: 'needs_update',
          adminNote: note || null,
          adminUpdatedAt: new Date().toISOString(),
        }));
        assignAdminState(nextStep);
        needsUpdateOpen.value = false;
        showActionMessage('Richiesta di aggiornamento salvata.');
        if (props.userId && percorsoContext.value.percorsoId) {
          try {
            await dispatchNeedsUpdateNotification({
              db,
              userUid: props.userId,
              programId: percorsoContext.value.percorsoId,
              stepKey: percorsoContext.value.stepKey || 'check_finale',
              step: nextStep,
              stepIndex: percorsoContext.value.stepIndex ?? null,
              programTitle: percorsoContext.value.programTitle || percorsoContext.value.programLabel || '',
              productName: percorsoContext.value.productName || '',
              sequenceIndex: percorsoContext.value.sequenceIndex ?? null,
              sequenceTotal: percorsoContext.value.sequenceTotal ?? null,
              adminNote: note,
              notifyUser: true,
              preserveOpenStatus: true,
              awaitNotification: false,
            });
          } catch (notifyErr) {
            console.error('dispatchNeedsUpdateNotification error', notifyErr);
          }
        }
      } catch (error) {
        console.error('saveNeedsUpdate error:', error);
        showActionMessage('Errore nel salvataggio della richiesta di aggiornamento.', 'error');
      } finally {
        adminSaving.value = false;
      }
    };

    const buildShareQuery = () => {
      const ctx = percorsoContext.value;
      const query = {};
      const assign = (key, value) => {
        if (value === undefined || value === null) return;
        const str = typeof value === 'string' ? value : String(value);
        if (!str) return;
        query[key] = str;
      };
      assign('percorsoId', ctx.percorsoId);
      if (Number.isInteger(ctx.stepIndex)) assign('stepIndex', ctx.stepIndex);
      assign('stepKey', ctx.stepKey);
      assign('userId', props.userId);
      const docId = questionarioDocId.value || manualDocLinkState.docId || '';
      assign('questionarioDocId', docId);
      const isManual = manualStandaloneView.value || !!manualDocLinkState.snapshot?.manualStandalone;
      if (isManual) assign('manualStandalone', '1');
      return query;
    };

    const buildShareLink = () => {
      const params = new URLSearchParams(buildShareQuery()).toString();
      const base = PORTAL_BASE_URL.replace(/\/$/, '');
      const hashPath = '/#/user-programmi/check-finale';
      return params ? `${base}${hashPath}?${params}` : `${base}${hashPath}`;
    };

    const copyFormLink = async () => {
      try {
        const link = buildShareLink();
        if (!link) throw new Error('Link non disponibile');
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(link);
        } else if (typeof document !== 'undefined') {
          const textarea = document.createElement('textarea');
          textarea.value = link;
          textarea.setAttribute('readonly', '');
          textarea.style.position = 'absolute';
          textarea.style.left = '-9999px';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        } else {
          throw new Error('Clipboard non supportata');
        }
        showActionMessage('Link copiato negli appunti.');
      } catch (err) {
        console.error('copyFormLink error:', err);
        showActionMessage('Impossibile copiare il link.', 'error');
      }
    };

    const togglePartialCheck = async () => {
      if (partialSaving.value) return;
      partialSaving.value = true;
      try {
        const docId = await ensureQuestionarioDoc();
        const nextValue = !questionarioPartial.value;
        await updateDoc(doc(db, QUESTIONARI_COLLECTION, docId), {
          allowPartial: nextValue,
          updatedAt: serverTimestamp(),
        });
        questionarioPartial.value = nextValue;
        showActionMessage(
          nextValue
            ? 'Check finale reso parziale: foto e misure non sono obbligatorie.'
            : 'Modalità parziale disattivata: foto e misure tornano obbligatorie.'
        );
      } catch (error) {
        console.error('togglePartialCheck error:', error);
        showActionMessage('Impossibile aggiornare la modalità parziale.', 'error');
      } finally {
        partialSaving.value = false;
      }
    };

    const exportFormAsPdf = async () => {
      if (pdfExporting.value) return;
      pdfExporting.value = true;
      showActionMessage('Sto generando il PDF…');
      try {
        const fileName = buildQuestionarioPdfFileName({
          prefix: 'check_finale',
          userId: effectiveUserId.value || questionarioDocId.value || 'utente',
          updatedAt: questionarioInfo.value.updatedAt,
        });
        await generateCheckFinalePdf({
          answers: plainAnswers(),
          meta: {
            userId: effectiveUserId.value || questionarioDocId.value || 'utente',
            userLabel: userDisplayName.value,
            questionarioInfo: questionarioInfo.value,
            percorso: percorsoContext.value,
          },
          fileName,
        });
        showActionMessage('PDF salvato nei download.');
      } catch (err) {
        console.error('exportFormAsPdf error:', err);
        showActionMessage('Impossibile generare il PDF.', 'error');
      } finally {
        pdfExporting.value = false;
      }
    };

    const plainAnswers = () => deepClone(form);
    const initialQuestionarioAnswers = ref({});
    const initialQuestionarioInfo = ref({ createdAt: null, updatedAt: null, templateLabel: null });
    const initialLoading = ref(false);
    const initialError = ref('');
    const initialSearch = ref('');
    const checkSearch = ref('');

    const currentAnswers = computed(() => plainAnswers());
    const sanitizedInitialAnswers = computed(() =>
      sanitizeQuestionarioAnswers(initialQuestionarioAnswers.value || {})
    );
    const sanitizedCheckAnswers = computed(() => sanitizeQuestionarioAnswers(currentAnswers.value || {}));
    const buildInitialSnapshotPayload = () => {
      const answers = sanitizedInitialAnswers.value || {};
      if (!Object.keys(answers).length) return {};
      const info = initialQuestionarioInfo.value || {};
      return {
        initialAnswersSnapshot: answers,
        initialQuestionarioInfoSnapshot: {
          createdAt: toIsoString(info.createdAt),
          updatedAt: toIsoString(info.updatedAt),
          templateLabel: info.templateLabel || null,
        },
      };
    };

    const groupedInitialEntries = computed(() =>
      groupEntries(flattenAnswers(sanitizedInitialAnswers.value), initialSearch.value, {
        answers: sanitizedInitialAnswers.value,
      })
    );

    const groupedCheckEntries = computed(() =>
      groupEntries(flattenAnswers(sanitizedCheckAnswers.value), checkSearch.value, {
        answers: sanitizedCheckAnswers.value,
      })
    );

    const setFormData = (data = {}, docData = null) => {
      const base = defaultForm();
      const source = { ...(data || {}) };

      Object.keys(base).forEach((key) => {
        if (key === 'foto') {
          const incoming = source.foto || {};
          const legacyProfile = incoming.profilo || null;
          base.foto.posteriore = incoming.posteriore || null;
          base.foto.anteriore = incoming.anteriore || null;
          base.foto.profiloDestra = incoming.profiloDestra || legacyProfile || null;
          base.foto.profiloSinistra = incoming.profiloSinistra || legacyProfile || null;
        } else {
          base[key] = source[key] !== undefined ? source[key] : base[key];
        }
      });

      suppressDirty.value = true;
      Object.assign(form, base);
      lastLoaded.value = deepClone(form);
      suppressDirty.value = false;

      if (docData) {
        questionarioInfo.value = {
          createdAt: toDateSafe(docData.createdAt) || null,
          updatedAt: toDateSafe(docData.updatedAt || docData.createdAt) || null,
          templateLabel: docData.templateLabel || QUESTIONARIO_TEMPLATE.label,
        };
      } else {
        questionarioInfo.value = { createdAt: null, updatedAt: null, templateLabel: QUESTIONARIO_TEMPLATE.label };
      }

      dirty.value = false;
      errorMessage.value = '';
    };

    const loadInitialQuestionario = async () => {
      initialLoading.value = true;
      initialError.value = '';
      initialQuestionarioAnswers.value = {};
      initialQuestionarioInfo.value = { createdAt: null, updatedAt: null, templateLabel: null };

      const uid = effectiveUserId.value;
      if (!uid) {
        initialLoading.value = false;
        return;
      }

      try {
        const ctx = percorsoContext.value;
        const constraints = [
          where('uid', '==', uid),
          where('type', '==', QUESTIONARIO_INITIAL_TYPE),
        ];
        if (ctx.percorsoId) constraints.push(where('percorsoId', '==', ctx.percorsoId));

        const candidates = [];

        try {
          const targetedSnap = await getDocs(
            query(collection(db, QUESTIONARI_COLLECTION), ...constraints, orderBy('updatedAt', 'desc'))
          );
          targetedSnap.forEach((docSnap) => {
            candidates.push({ docSnap, data: docSnap.data() || {} });
          });
        } catch (err) {
          if (err?.code === 'failed-precondition') {
            const fallbackSnap = await getDocs(query(collection(db, QUESTIONARI_COLLECTION), ...constraints));
            fallbackSnap.forEach((docSnap) => {
              candidates.push({ docSnap, data: docSnap.data() || {} });
            });
          } else {
            throw err;
          }
        }

        if (candidates.length) {
          candidates.sort((a, b) => {
            const aTime = (toDateSafe(a.data.updatedAt || a.data.createdAt) || new Date(0)).getTime();
            const bTime = (toDateSafe(b.data.updatedAt || b.data.createdAt) || new Date(0)).getTime();
            return bTime - aTime;
          });

          const latest = candidates[0].data;
          initialQuestionarioAnswers.value = latest.answers || {};
          initialQuestionarioInfo.value = {
            createdAt: toDateSafe(latest.createdAt) || null,
            updatedAt: toDateSafe(latest.updatedAt || latest.createdAt) || null,
            templateLabel:
              latest.templateLabel ||
              latest.questionarioTemplateLabel ||
              latest.questionarioTemplate ||
              'Questionario Programmi',
          };
        } else {
          initialQuestionarioAnswers.value = {};
          initialQuestionarioInfo.value = { createdAt: null, updatedAt: null, templateLabel: null };
        }
      } catch (error) {
        console.error('Errore caricando il questionario iniziale:', error);
        initialQuestionarioAnswers.value = {};
        initialQuestionarioInfo.value = { createdAt: null, updatedAt: null, templateLabel: null };
        initialError.value = 'Impossibile recuperare il questionario iniziale.';
      } finally {
        initialLoading.value = false;
      }
    };

    watch(
      form,
      () => {
        if (!suppressDirty.value) dirty.value = true;
      },
      { deep: true }
    );

    watch(
      () => form.integrazioneAssunta,
      (value) => {
        if (value !== 'Si') form.integrazioneDettagli = '';
      }
    );

    watch(
      () => form.recuperoSonnoContinuativo,
      (value) => {
        if (value !== 'No') form.recuperoSonnoMotivo = '';
      }
    );

    const buildQuestionarioMetadata = () => {
      const ctx = percorsoContext.value;
      return {
        percorsoId: ctx.percorsoId || null,
        stepIndex: Number.isInteger(ctx.stepIndex) ? ctx.stepIndex : null,
        stepKey: ctx.stepKey || null,
        sequenceGroupId: ctx.sequenceGroupId || null,
        sequenceIndex: Number.isInteger(ctx.sequenceIndex) ? ctx.sequenceIndex : null,
        sequenceTotal: Number.isInteger(ctx.sequenceTotal) ? ctx.sequenceTotal : null,
        productId: ctx.productId || null,
        productName: ctx.productName || null,
        programTitle: ctx.programTitle || ctx.programLabel || null,
        programLabel: ctx.programLabel || ctx.programTitle || null,
        plannedStartAt: ctx.plannedStartAt || null,
        expireAt: ctx.expireAt || null,
        stepName: ctx.stepName || null,
        orderRef: ctx.orderRef || null,
      };
    };

    const writeTextToClipboard = async (text) => {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }
      if (typeof document === 'undefined') throw new Error('Clipboard non supportata');
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (!copied) throw new Error('Impossibile copiare negli appunti');
    };

    const readTextFromClipboard = async () => {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.readText) {
        return navigator.clipboard.readText();
      }
      if (typeof window !== 'undefined') {
        const manual = window.prompt('Incolla qui il check finale copiato e premi OK:');
        if (manual === null) throw new Error('clipboard_read_cancelled');
        return manual;
      }
      throw new Error('Clipboard non supportata');
    };

    const buildClipboardPayload = () => {
      const info = questionarioInfo.value || {};
      const template = {
        id: QUESTIONARIO_TEMPLATE.id,
        key: QUESTIONARIO_TEMPLATE.key,
        label: QUESTIONARIO_TEMPLATE.label,
        version: QUESTIONARIO_TEMPLATE.version,
      };
      return {
        type: QUESTIONARIO_TYPE,
        template,
        questionarioDocId: questionarioDocId.value || null,
        userId: effectiveUserId.value || null,
        exportedAt: new Date().toISOString(),
        metadata: buildQuestionarioMetadata(),
        questionarioInfo: {
          createdAt: toIsoString(info.createdAt),
          updatedAt: toIsoString(info.updatedAt),
        },
        answers: plainAnswers(),
      };
    };

    const applyClipboardPayload = (payload) => {
      if (!payload || typeof payload !== 'object') {
        throw new Error('Il contenuto copiato non è valido.');
      }
      if (payload.type && payload.type !== QUESTIONARIO_TYPE) {
        throw new Error('I dati copiati appartengono a un altro questionario.');
      }
      if (!payload.answers || typeof payload.answers !== 'object') {
        throw new Error('Il contenuto copiato non contiene risposte.');
      }
      const previousBaseline = deepClone(lastLoaded.value);
      setFormData(payload.answers, payload);
      lastLoaded.value = previousBaseline;
      dirty.value = true;
      showActionMessage('Check finale incollato. Salva per confermare.');
    };

    const copyQuestionarioAnswers = async () => {
      if (copyingAnswers.value) return;
      copyingAnswers.value = true;
      try {
        const payload = buildClipboardPayload();
        const serialized = JSON.stringify(payload, null, 2);
        await writeTextToClipboard(serialized);
        showActionMessage('Check finale copiato negli appunti.');
      } catch (error) {
        console.error('copyQuestionarioAnswers error:', error);
        showActionMessage('Impossibile copiare il check finale.', 'error');
      } finally {
        copyingAnswers.value = false;
      }
    };

    const pasteQuestionarioAnswers = async () => {
      if (pastingAnswers.value) return;
      pastingAnswers.value = true;
      try {
        const rawText = await readTextFromClipboard();
        const trimmed = (rawText || '').trim();
        if (!trimmed) throw new Error('Gli appunti sono vuoti.');
        let payload;
        try {
          payload = JSON.parse(trimmed);
        } catch (parseError) {
          throw new Error('Il contenuto copiato non è un JSON valido.');
        }
        applyClipboardPayload(payload);
      } catch (error) {
        if (error?.message === 'clipboard_read_cancelled') {
          // Operazione annullata dall'utente.
        } else {
          console.error('pasteQuestionarioAnswers error:', error);
          showActionMessage(error.message || 'Impossibile incollare il check finale.', 'error');
        }
      } finally {
        pastingAnswers.value = false;
      }
    };

    const buildQuestionarioUpdatePayload = (extra = {}) => ({
      ...buildQuestionarioMetadata(),
      questionarioUid: questionarioDocId.value || null,
      updatedAt: serverTimestamp(),
      ...extra,
    });

    const syncQuestionarioStepMetadata = async (docId, overrides = {}) => {
      const ctx = percorsoContext.value;
      if (!docId || !ctx.percorsoId || ctx.stepIndex == null || ctx.stepIndex < 0) return;
      try {
        const percorsoRef = doc(db, 'percorsi', ctx.percorsoId);
        const snap = await getDoc(percorsoRef);
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const steps = Array.isArray(data.steps) ? [...data.steps] : [];
        const idx = ctx.stepIndex;
        const prev = steps[idx] || {};
        const prevQuestionnaire = prev.questionnaire || {};
        const mergedMeta = {
          ...prevQuestionnaire,
          ...buildQuestionarioMetadata(),
          ...overrides,
          id: docId,
          type: QUESTIONARIO_TYPE,
          questionarioUid: docId,
          orderRef: overrides.orderRef || prevQuestionnaire.orderRef || buildQuestionarioMetadata().orderRef || null,
        };

        if (!mergedMeta.questionarioKey) {
          const qKey =
            mergedMeta.percorsoId && Number.isInteger(mergedMeta.stepIndex)
              ? `${mergedMeta.percorsoId}__${mergedMeta.stepIndex}`
              : mergedMeta.percorsoId && mergedMeta.stepKey
              ? `${mergedMeta.percorsoId}__${mergedMeta.stepKey}`
              : null;
          mergedMeta.questionarioKey = qKey;
        }

        if (overrides.updatedAt) {
          mergedMeta.updatedAt = overrides.updatedAt instanceof Date ? overrides.updatedAt.toISOString() : overrides.updatedAt;
        }

        if (overrides.createdAt) {
          mergedMeta.createdAt = overrides.createdAt instanceof Date ? overrides.createdAt.toISOString() : overrides.createdAt;
        } else if (!mergedMeta.createdAt && questionarioInfo.value.createdAt) {
          mergedMeta.createdAt = questionarioInfo.value.createdAt.toISOString();
        }

        mergedMeta.templateId = overrides.templateId || mergedMeta.templateId || QUESTIONARIO_TEMPLATE.id;
        mergedMeta.templateKey = overrides.templateKey || mergedMeta.templateKey || QUESTIONARIO_TEMPLATE.key;
        mergedMeta.templateLabel = overrides.templateLabel || mergedMeta.templateLabel || QUESTIONARIO_TEMPLATE.label;
        mergedMeta.templateVersion = overrides.templateVersion ?? mergedMeta.templateVersion ?? QUESTIONARIO_TEMPLATE.version;

        steps[idx] = {
          ...prev,
          questionarioDocId: docId,
          questionnaire: mergedMeta,
        };

        await updateDoc(percorsoRef, { steps });
      } catch (error) {
        console.error('Errore aggiornando metadati check finale nel percorso:', error);
      }
    };

    const maybeBackfillQuestionarioDoc = async (docId, data = {}) => {
      if (!docId) return;
      const metadata = buildQuestionarioMetadata();
      const patch = {};
      let needsPatch = false;

      Object.entries(metadata).forEach(([key, value]) => {
        if (value == null) return;
        const existing = data[key];
        if (existing == null || (typeof existing === 'string' && !existing.trim())) {
          patch[key] = value;
          needsPatch = true;
        }
      });

      if (!data.questionarioUid) {
        patch.questionarioUid = docId;
        needsPatch = true;
      }

      if (needsPatch) {
        try {
          await updateDoc(doc(db, QUESTIONARI_COLLECTION, docId), patch);
        } catch (err) {
          console.warn('Impossibile aggiornare i metadati del check finale:', err);
        }
      }
    };

    const detachManualQuestionarioDoc = async () => {
      if (!manualDocLinkState.shouldDetach || !questionarioDocId.value) return questionarioDocId.value;
      const ctx = percorsoContext.value;
      if (!ctx.percorsoId || !Number.isInteger(ctx.stepIndex)) {
        resetManualDocLinkState();
        return questionarioDocId.value;
      }
      try {
        const snapshotData = manualDocLinkState.snapshot || {};
        const newDocRef = doc(collection(db, QUESTIONARI_COLLECTION));
        const metadata = buildQuestionarioMetadata();
        const templateInfo = {
          templateId: snapshotData.templateId || QUESTIONARIO_TEMPLATE.id,
          templateKey: snapshotData.templateKey || QUESTIONARIO_TEMPLATE.key,
          templateLabel: snapshotData.templateLabel || QUESTIONARIO_TEMPLATE.label,
          templateVersion: snapshotData.templateVersion ?? QUESTIONARIO_TEMPLATE.version,
        };
        const createdAtValue =
          snapshotData.createdAt || questionarioInfo.value.createdAt || new Date();
        const updatedAtValue = new Date();
        const questionarioKey =
          (metadata.percorsoId && Number.isInteger(metadata.stepIndex)
            ? `${metadata.percorsoId}__${metadata.stepIndex}`
            : metadata.percorsoId && metadata.stepKey
            ? `${metadata.percorsoId}__${metadata.stepKey}`
            : null);
        const payload = {
          ...snapshotData,
          ...metadata,
          ...templateInfo,
          uid: effectiveUserId.value,
          answers: plainAnswers(),
          allowPartial: snapshotData.allowPartial ?? questionarioPartial.value,
          manualStandalone: false,
          questionarioUid: newDocRef.id,
          questionarioKey: questionarioKey || snapshotData.questionarioKey || null,
          createdAt: createdAtValue,
          updatedAt: updatedAtValue,
        };
        payload.type = snapshotData.type || QUESTIONARIO_TYPE;
        delete payload.id;
        await setDoc(newDocRef, payload);

        questionarioDocId.value = newDocRef.id;
        questionarioPartial.value = !!payload.allowPartial;
        questionarioInfo.value = {
          createdAt: toDateSafe(createdAtValue) || null,
          updatedAt: updatedAtValue,
          templateLabel: payload.templateLabel || QUESTIONARIO_TEMPLATE.label,
        };
        resetManualDocLinkState();

        try {
          await router.replace({
            name: route.name || 'UserProgrammiCheckFinale',
            params: route.params,
            query: { ...route.query, questionarioDocId: newDocRef.id },
          });
        } catch (navError) {
          console.warn('Impossibile aggiornare l\'URL del check finale duplicato:', navError);
        }

        await syncQuestionarioStepMetadata(newDocRef.id, {
          ...templateInfo,
          createdAt: toDateSafe(createdAtValue) || undefined,
          updatedAt: updatedAtValue,
          questionarioKey: questionarioKey || undefined,
          questionarioUid: newDocRef.id,
        });

        return newDocRef.id;
      } catch (error) {
        console.error('Errore durante la copia automatica del check finale manuale:', error);
        throw error;
      }
    };

    const normalizeStepKey = (value) => String(value || '').trim().toLowerCase() || 'check_finale';

    const findReusableManualDocId = async () => {
      const uid = effectiveUserId.value;
      if (!uid) return null;
      const stepKey = normalizeStepKey(percorsoContext.value.stepKey || 'check_finale');
      try {
        const snap = await getDocs(query(collection(db, QUESTIONARI_COLLECTION), where('uid', '==', uid)));
        const candidates = [];
        snap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          if (data.percorsoId) return;
          const key = normalizeStepKey(data.stepKey || data.questionarioStepKey || '');
          if (key && key !== stepKey) return;
          const looksManual =
            data.manualStandalone === true ||
            (!data.percorsoId && !data.sequenceGroupId && !data.sequenceIndex && !data.sequenceTotal);
          if (!looksManual) return;
          const ts = toDateSafe(data.updatedAt || data.createdAt) || new Date(0);
          candidates.push({ id: docSnap.id, ts });
        });
        if (!candidates.length) return null;
        candidates.sort((a, b) => b.ts.getTime() - a.ts.getTime());
        return candidates[0].id;
      } catch (err) {
        console.warn('findReusableManualDocId (check) error', err);
        return null;
      }
    };

    const ensureAuthForLink = async () => {
      if (auth.currentUser) {
        if (!currentUserId.value) currentUserId.value = auth.currentUser.uid;
        return;
      }
      const token = customToken.value;
      if (!token) return;
      try {
        await signInWithCustomToken(auth, token);
        if (!currentUserId.value && auth.currentUser?.uid) {
          currentUserId.value = auth.currentUser.uid;
        }
      } catch (err) {
        console.error('signInWithCustomToken (check) error', err);
        errorMessage.value = 'Accesso non riuscito. Accedi dal link o richiedi un nuovo accesso.';
      }
    };

    const ensureQuestionarioDoc = async () => {
      if (questionarioDocId.value && manualDocLinkState.shouldDetach) {
        await detachManualQuestionarioDoc();
      }
      if (!questionarioDocId.value && manualStandaloneView.value) {
        const reusableId = await findReusableManualDocId();
        if (reusableId) {
          questionarioDocId.value = reusableId;
          try {
            const nextQuery = { ...route.query, questionarioDocId: reusableId };
            router.replace({ query: nextQuery }).catch(() => {});
          } catch (navErr) {
            console.warn('Impossibile aggiornare la query con il check riutilizzato', navErr);
          }
          return reusableId;
        }
      }
      if (questionarioDocId.value) return questionarioDocId.value;
      const uid = effectiveUserId.value || toTrimmedStringOrNull(pickQueryValue('userId', route));
      if (!uid) throw new Error('ID utente non disponibile.');
      const docRef = doc(collection(db, QUESTIONARI_COLLECTION));
      const metadata = buildQuestionarioMetadata();
      await setDoc(docRef, {
        uid,
        type: QUESTIONARIO_TYPE,
        templateId: QUESTIONARIO_TEMPLATE.id,
        templateKey: QUESTIONARIO_TEMPLATE.key,
        templateLabel: QUESTIONARIO_TEMPLATE.label,
        templateVersion: QUESTIONARIO_TEMPLATE.version,
        answers: plainAnswers(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...metadata,
        ...buildInitialSnapshotPayload(),
        questionarioUid: docRef.id,
      });
      const now = new Date();
      questionarioDocId.value = docRef.id;
      questionarioInfo.value = { createdAt: now, updatedAt: now, templateLabel: QUESTIONARIO_TEMPLATE.label };
      questionarioPartial.value = false;
      await syncQuestionarioStepMetadata(docRef.id, {
        createdAt: now,
        updatedAt: now,
      });
      return docRef.id;
    };

    const loadQuestionario = async () => {
      const uid = effectiveUserId.value;
      loading.value = true;
      errorMessage.value = '';

      if (!uid) {
        errorMessage.value = 'ID utente non disponibile.';
        await loadInitialQuestionario();
        loading.value = false;
        resetManualDocLinkState();
        return;
      }

      try {
        const ctx = percorsoContext.value;
        const candidateMap = new Map();

        if (ctx.percorsoId && ctx.stepKey) {
          const constraints = [
            where('uid', '==', uid),
            where('type', '==', QUESTIONARIO_TYPE),
            where('percorsoId', '==', ctx.percorsoId),
            where('stepKey', '==', ctx.stepKey),
          ];
          if (Number.isInteger(ctx.stepIndex)) {
            constraints.push(where('stepIndex', '==', ctx.stepIndex));
          }
          const targetedSnap = await getDocs(query(collection(db, QUESTIONARI_COLLECTION), ...constraints));
          targetedSnap.forEach((docSnap) => {
            candidateMap.set(docSnap.id, docSnap);
          });
        }

        if (!candidateMap.size) {
          try {
            const orderedSnap = await getDocs(
              query(
                collection(db, QUESTIONARI_COLLECTION),
                where('uid', '==', uid),
                where('type', '==', QUESTIONARIO_TYPE),
                orderBy('updatedAt', 'desc')
              )
            );
            orderedSnap.forEach((docSnap) => candidateMap.set(docSnap.id, docSnap));
          } catch (err) {
            if (err?.code === 'failed-precondition') {
              const fallbackSnap = await getDocs(
                query(
                  collection(db, QUESTIONARI_COLLECTION),
                  where('uid', '==', uid),
                  where('type', '==', QUESTIONARIO_TYPE)
                )
              );
              fallbackSnap.forEach((docSnap) => candidateMap.set(docSnap.id, docSnap));
            } else {
              throw err;
            }
          }
        }

        const candidateDocs = Array.from(candidateMap.values()).map((docSnap) => {
          const data = docSnap.data() || {};
          const updated = toDateSafe(data.updatedAt || data.createdAt);
          return { docSnap, data, sortValue: updated ? updated.getTime() : 0 };
        });

        candidateDocs.sort((a, b) => b.sortValue - a.sortValue);

        const ctxForSelection = ctx;
        const wantSpecificStep = !!(ctxForSelection.percorsoId && ctxForSelection.stepKey);
        const wantSpecificIndex = Number.isInteger(ctxForSelection.stepIndex);
        const pickCandidate = () => {
          if (!candidateDocs.length) return null;
          if (!wantSpecificStep) return candidateDocs[0];

          const strictMatch = candidateDocs.find(({ data }) => {
            const meta = data || {};
            if (meta.percorsoId && ctxForSelection.percorsoId && meta.percorsoId !== ctxForSelection.percorsoId) return false;
            if (meta.stepKey && meta.stepKey !== ctxForSelection.stepKey) return false;
            if (wantSpecificIndex) {
              const docIndex = toStepIndex(meta.stepIndex);
              return docIndex === ctxForSelection.stepIndex;
            }
            return true;
          });
          if (strictMatch) return strictMatch;

          if (!wantSpecificIndex) {
            return candidateDocs.find(({ data }) => {
              const meta = data || {};
              if (meta.percorsoId && ctxForSelection.percorsoId && meta.percorsoId !== ctxForSelection.percorsoId) return false;
              if (meta.stepKey && meta.stepKey !== ctxForSelection.stepKey) return false;
              return true;
            }) || null;
          }

          return null;
        };

        const selected = pickCandidate();

        if (selected) {
          const { docSnap, data } = selected;
          questionarioDocId.value = docSnap.id;
          setFormData(data.answers || {}, data);
          questionarioPartial.value = !!data.allowPartial;
          await maybeBackfillQuestionarioDoc(docSnap.id, data);
          await syncQuestionarioStepMetadata(docSnap.id, {
            templateId: data.templateId || QUESTIONARIO_TEMPLATE.id,
            templateKey: data.templateKey || data.questionarioTemplateKey || QUESTIONARIO_TEMPLATE.key,
            templateLabel: data.templateLabel || QUESTIONARIO_TEMPLATE.label,
            templateVersion: data.templateVersion ?? QUESTIONARIO_TEMPLATE.version,
            createdAt: toDateSafe(data.createdAt) || undefined,
            updatedAt: toDateSafe(data.updatedAt || data.createdAt) || undefined,
            questionarioUid: data.questionarioUid || docSnap.id,
          });
          evaluateManualDocLinkState(docSnap.id, data);
        } else {
          questionarioDocId.value = '';
          setFormData({}, null);
          questionarioPartial.value = false;
          resetManualDocLinkState();
        }
      } catch (error) {
        console.error('Errore caricando il check finale:', error);
        errorMessage.value = 'Impossibile recuperare il check finale.';
        questionarioDocId.value = '';
        setFormData({}, null);
        questionarioPartial.value = false;
        resetManualDocLinkState();
      } finally {
        await loadInitialQuestionario();
        loading.value = false;
      }
    };

    const loadWithAuth = async () => {
      await ensureAuthForLink();
      await loadQuestionario();
    };

    const saveQuestionario = async () => {
      if (saving.value || loading.value) return;
      const uid = effectiveUserId.value;
      if (!uid) {
        errorMessage.value = 'ID utente non disponibile.';
        return;
      }

      saving.value = true;
      errorMessage.value = '';

      try {
        const docId = await ensureQuestionarioDoc();
        const docRef = doc(db, QUESTIONARI_COLLECTION, docId);
        await updateDoc(docRef, {
          answers: plainAnswers(),
          templateVersion: QUESTIONARIO_TEMPLATE.version,
          templateLabel: QUESTIONARIO_TEMPLATE.label,
          updatedAt: serverTimestamp(),
          questionarioUid: docId,
          ...buildInitialSnapshotPayload(),
          ...buildQuestionarioMetadata(),
        });

        dirty.value = false;
        lastLoaded.value = deepClone(form);
        const now = new Date();
        questionarioInfo.value = {
          createdAt: questionarioInfo.value.createdAt || now,
          updatedAt: now,
          templateLabel: QUESTIONARIO_TEMPLATE.label,
        };
        await syncQuestionarioStepMetadata(docId, { updatedAt: now });

        showActionMessage('Il check è stato salvato correttamente', 'success');
      } catch (error) {
        console.error('Errore nel salvataggio del check finale:', error);
        errorMessage.value = 'Errore durante il salvataggio del check finale.';
      } finally {
        saving.value = false;
      }
    };

    const resetForm = () => {
      suppressDirty.value = true;
      Object.assign(form, deepClone(lastLoaded.value));
      suppressDirty.value = false;
      dirty.value = false;
      errorMessage.value = '';
    };

    const completeCheckStep = async () => {
      if (completingStep.value || saving.value || loading.value) return;
      const ctx = percorsoContext.value;
      if (!ctx.percorsoId || !Number.isInteger(ctx.stepIndex)) {
        showActionMessage('Collega questo check a un percorso per inviarlo.', 'error');
        return;
      }
      try {
        completingStep.value = true;
        if (dirty.value || !questionarioDocId.value || manualDocLinkState.shouldDetach) {
          await saveQuestionario();
        }
        if (!questionarioDocId.value) {
          showActionMessage('Salva il check prima di inviarlo.', 'error');
          return;
        }
        await setStepDone(ctx.percorsoId, ctx.stepIndex, true);
        showActionMessage('Check segnato come inviato, adesso lo troverai in questionari da lavorare');
      } catch (error) {
        console.error('completeCheckStep error:', error);
        showActionMessage('Impossibile segnare il check come inviato.', 'error');
      } finally {
        completingStep.value = false;
      }
    };

    const uploadSingleFile = async (segments, file) => {
      const uid = effectiveUserId.value;
      if (!uid) {
        errorMessage.value = 'ID utente non disponibile.';
        showActionMessage('Sessione scaduta: effettua nuovamente il login.', 'error');
        throw new Error('ID utente non disponibile.');
      }
      const docId = await ensureQuestionarioDoc();
      const fileName = `${Date.now()}_${sanitizeName(file.name)}`;
      const path = ['documenti', uid, 'questionari', docId, ...segments, fileName].join('/');
      const ref = storageRef(storage, path);
      await uploadBytesResumable(ref, file);
      const url = await getDownloadURL(ref);
      return { name: file.name, size: file.size, contentType: file.type || null, path, url };
    };

    const onPhotoChange = async (fieldKey, event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      setUploadState(uploadingPhoto, fieldKey, true);
      try {
        const metadata = await uploadSingleFile(['foto', fieldKey], file);
        suppressDirty.value = true;
        form.foto[fieldKey] = metadata;
        suppressDirty.value = false;
        await updateDoc(
          doc(db, QUESTIONARI_COLLECTION, questionarioDocId.value),
          buildQuestionarioUpdatePayload({ answers: plainAnswers() })
        );
        lastLoaded.value = deepClone(form);
        await syncQuestionarioStepMetadata(questionarioDocId.value, { updatedAt: new Date() });
      } catch (error) {
        console.error('Errore caricando la foto:', error);
        if (!errorMessage.value) {
          errorMessage.value = 'Errore durante il caricamento della foto.';
          showActionMessage(errorMessage.value, 'error');
        }
      } finally {
        setUploadState(uploadingPhoto, fieldKey, false);
      }
    };

    const removePhoto = async (fieldKey) => {
      const metadata = form.foto[fieldKey];
      if (!metadata) return;
      if (typeof window !== 'undefined') {
        const confirmDelete = window.confirm(`Eliminare "${metadata.name}"?`);
        if (!confirmDelete) return;
      }
      try {
        await deleteObject(storageRef(storage, metadata.path));
        suppressDirty.value = true;
        form.foto[fieldKey] = null;
        suppressDirty.value = false;
        await updateDoc(
          doc(db, QUESTIONARI_COLLECTION, questionarioDocId.value),
          buildQuestionarioUpdatePayload({ answers: plainAnswers() })
        );
        lastLoaded.value = deepClone(form);
        await syncQuestionarioStepMetadata(questionarioDocId.value, { updatedAt: new Date() });
      } catch (error) {
        console.error('Errore eliminando la foto:', error);
        errorMessage.value = 'Errore durante l\'eliminazione della foto.';
      }
    };

    watch(
      () => `${percorsoContext.value.percorsoId || 'nop'}__${
        Number.isInteger(percorsoContext.value.stepIndex) ? percorsoContext.value.stepIndex : 'x'
      }`,
      () => {
        refreshAdminState();
      },
      { immediate: true },
    );

    watch(questionarioTargetKey, (next, prev) => {
      if (prev !== undefined && next !== prev) {
        loadWithAuth();
      }
    });

    watch(
      () => props.userId,
      (next) => {
        if (next && next !== currentUserId.value) {
          currentUserId.value = next;
          loadWithAuth();
        }
      }
    );

    watch(
      () => route.params.userId,
      (next) => {
        if (next && next !== currentUserId.value) {
          currentUserId.value = next;
          loadWithAuth();
        }
      }
    );

    watch(
      () => toTrimmedStringOrNull(pickQueryValue('userId', route)),
      (next) => {
        if (next && next !== currentUserId.value) {
          currentUserId.value = next;
          loadWithAuth();
        }
      },
    );

    watch(
      () => customToken.value,
      (next, prev) => {
        if (next && next !== prev) {
          loadWithAuth();
        }
      },
    );

    onMounted(() => {
      authUnsubscribe = onAuthStateChanged(auth, (user) => {
        if (user && !currentUserId.value) {
          currentUserId.value = user.uid;
        }
      });
      loadWithAuth();
    });

    onBeforeUnmount(() => {
      if (typeof authUnsubscribe === 'function') authUnsubscribe();
      toastTimers.forEach(clearTimeout);
      toastTimers.clear();
    });

    return {
      form,
      loading,
      saving,
      completingStep,
      dirty,
      errorMessage,
      toasts,
      questionarioInfo,
      questionarioPartial,
      partialSaving,
      adminState,
      adminLoading,
      adminSaving,
      adminStatusLabel,
      adminChipClass,
      adminError,
      canManageAdmin,
      needsUpdateOpen,
      needsUpdateNote,
      handleAdminApprove,
      toggleNeedsUpdate,
      saveNeedsUpdateRequest,
      exportFormAsPdf,
      copyQuestionarioAnswers,
      pasteQuestionarioAnswers,
      copyFormLink,
      togglePartialCheck,
      completeCheckStep,
      dismissToast,
      pdfExporting,
      copyingAnswers,
      pastingAnswers,
      printableCard,
      uploadingPhoto,
      isImageFile,
      effectiveUserId,
      SEDUTE_SETTIMANALI_OPTIONS,
      ADESIONE_PERCENT_OPTIONS,
      STRATEGIA_ALIMENTARE_OPTIONS,
      RISPETTO_GRAMMATURE_OPTIONS,
      PASTI_LIBERI_OPTIONS,
      ACQUA_MEDIA_OPTIONS,
      ORE_SONNO_OPTIONS,
      TREND_SONNO_OPTIONS,
      YES_NO_OPTIONS,
      formatDate,
      initialQuestionarioInfo,
      initialLoading,
      initialError,
      initialSearch,
      checkSearch,
      groupedInitialEntries,
      groupedCheckEntries,
      prettifyKey,
      saveQuestionario,
      resetForm,
      onPhotoChange,
      removePhoto,
    };
  },
};
</script>

<style scoped>

.textarea {
  max-width: 90%;

}
.dashboard-layout {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 100vh;
  background: linear-gradient(180deg, #eef1ff 0%, #f7f8ff 100%);
}

.content-container {
  background: linear-gradient(180deg, rgba(243, 245, 255, 0.98) 0%, #eef1ff 100%);
  padding-right: 42px;
  padding-bottom: 64px;
}

.questionario-content {
  width: min(1140px, 100%);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.questionario-card {
  width: 97%;
  display: flex;
  flex-direction: column;
  gap: 32px;
  background: #ffffff;
  border-radius: 28px;
  border: 1px solid rgba(75, 98, 192, 0.14);
  box-shadow: 0 36px 96px -64px rgba(18, 26, 70, 0.5);
  padding: 36px clamp(28px, 5vw, 48px);
  text-align: left;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: min(4vw, 26px);
  height: 37px;
}

.card-header__title {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: min(72%, 640px);
}

.card-header h1 {
  margin: 0;
  font-size: clamp(1.8rem, 2.2vw, 2.2rem);
  font-weight: 700;
  color: #eeeeee;
  letter-spacing: -0.01em;
}

.muted {
  color: #48527a;
  font-size: 0.95rem;
}

.meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  color: #2c2c2c;
  background: rgba(200, 200, 200, 0.944);
  border-radius: 999px;
  padding: 6px 14px;
}

.card-header__right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.card-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.ghost-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 6px 14px;
  border: 1px solid rgba(208, 208, 208, 0.531);
  border-radius: 8px;
  background: transparent;
  color: #ffffff;
  font-size: 0.9rem;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.ghost-button:hover {
  background: rgba(34, 45, 89, 0.1);
  color: #1a2550;
  border-color: rgba(34, 45, 89, 0.35);
}

.ghost-button__spinner {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
  animation: ghost-button-spin 0.9s linear infinite;
}

@keyframes ghost-button-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.admin-wrapper {
  margin-bottom: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.admin-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  background: #f8fafc;
}

.admin-controls__status {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.admin-chip {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  background: #e2e8f0;
  color: #0f172a;
}

.chip-green {
  background: #dcfce7;
  color: #14532d;
}

.chip-orange {
  background: #fef3c7;
  color: #92400e;
}

.chip-neutral {
  background: #e2e8f0;
  color: #334155;
}

.admin-status-note {
  font-size: 0.82rem;
  color: #475569;
}

.admin-controls__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.admin-btn {
  border: none;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.admin-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.admin-btn--green {
  background: #16a34a;
  color: #ffffff;
}

.admin-btn--orange {
  background: #f97316;
  color: #ffffff;
}

.admin-btn--blue {
  background: #2563eb;
  color: #ffffff;
}

.admin-btn--grey {
  background: #e2e8f0;
  color: #1f2937;
}

.admin-needs-box {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  padding: 12px 14px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.admin-note-input {
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #cbd5f5;
  font-size: 0.9rem;
}

.admin-note {
  margin: 0;
  font-size: 0.88rem;
  color: #475569;
}

.admin-helper {
  margin: 12px 0 0;
  font-size: 0.9rem;
}

.admin-error {
  margin: 8px 0 0;
  font-size: 0.85rem;
  color: #b91c1c;
}

.state {
  padding: 12px 16px;
  border-radius: 14px;
  font-size: 0.95rem;
  font-weight: 600;
}

.state-loading {
  background: rgba(76, 120, 255, 0.12);
  color: #2b5de0;
}

.state-error {
  background: rgba(237, 93, 93, 0.12);
  color: #c02134;
}

.state-success {
  background: rgba(53, 214, 126, 0.14);
  color: #1d7a4c;
}

.qi-form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.qi-block {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 23px clamp(22px, 4vw, 36px);
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(250, 251, 255, 0.98) 0%, #ffffff 100%);
  border: 1px solid rgba(79, 99, 191, 0.18);
  box-shadow: inset 0 0 0 1px rgba(102, 121, 210, 0.06);
  margin-top: 13px;
}

.qi-block__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.qi-block__header-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: min(75%, 620px);
}

.qi-block__title {
  margin: 0;
  font-size: clamp(1.28rem, 2vw, 1.4rem);
  font-weight: 600;
  color: #16285d;
  letter-spacing: -0.01em;
}

.qi-block__subtitle {
  margin: 0;
  font-size: 0.94rem;
  line-height: 1.5;
}

.qi-block__body {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.grid {
  display: grid;
  gap: 18px;
}

.grid.two {
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.grid.three {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

label {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-weight: 500;
  color: #15204a;
}

label input,
label select,
label textarea {
  border: 1.4px solid #cfd4ff;
  border-radius: 14px;
  padding: 12px 14px;
  font-size: 1rem;
  color: #15204a;
  background: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.12s ease;
}

label input:focus,
label select:focus,
label textarea:focus {
  outline: none;
  border-color: #4c6cff;
  box-shadow: 0 0 0 3px rgba(76, 108, 255, 0.2);
  transform: translateY(-1px);
}

label textarea {
  resize: vertical;
  max-height: 80px;
}


.checkbox-group,
.equipment-group {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px;
  border-radius: 18px;
  background: linear-gradient(145deg, rgba(76, 108, 255, 0.08), rgba(76, 108, 255, 0.02));
  border: 1px solid rgba(76, 108, 255, 0.12);
}

.checkbox-item {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: 12px;
  padding: 4px 0;
  color: #18254f;
  cursor: pointer;
}

.checkbox-item span {
  line-height: 1.45;
}

.checkbox-item input[type='checkbox'] {
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(76, 108, 255, 0.4);
  border-radius: 6px;
  background: #ffffff;
  display: grid;
  place-items: center;
  margin: 0;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.checkbox-item input[type='checkbox']::after {
  content: '';
  width: 9px;
  height: 9px;
  border-radius: 3px;
  background: linear-gradient(135deg, #4663ff, #354de3);
  transform: scale(0);
  transition: transform 0.2s ease;
}

.checkbox-item input[type='checkbox']:checked {
  border-color: rgba(70, 97, 238, 0.9);
  background: rgba(70, 97, 238, 0.08);
  box-shadow: 0 0 0 3px rgba(70, 97, 238, 0.12);
}

.checkbox-item input[type='checkbox']:checked::after {
  transform: scale(1);
}

.checkbox-item input[type='checkbox']:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px rgba(70, 97, 238, 0.25);
}

.group-label {
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #16285d;
}

.equipment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
  justify-items: start;
}

.equipment-group,
.equipment-group span,
.certification-panel span,
.certification-panel label span {
  color: #16285d;
}

.equipment-pill {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 12px 18px 12px 46px;
  border: 1px solid rgba(76, 108, 255, 0.25);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.95);
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.equipment-pill:hover {
  border-color: rgba(76, 108, 255, 0.6);
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 8px 18px rgba(76, 108, 255, 0.12);
  transform: translateY(-1px);
}

.equipment-pill input[type='checkbox'] {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.equipment-pill input[type='checkbox']:focus-visible + .equipment-label::before {
  box-shadow: 0 0 0 4px rgba(70, 97, 238, 0.25);
}

.equipment-label {
  position: relative;
  font-weight: 600;
  color: #16285d;
  letter-spacing: 0.01em;
}

.equipment-label::before,
.equipment-label::after {
  content: '';
  position: absolute;
  left: -28px;
  top: 50%;
  transform: translateY(-50%);
  transition: all 0.2s ease;
}

.equipment-label::before {
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 2px solid rgba(76, 108, 255, 0.45);
  background: #ffffff;
}

.equipment-label::after {
  width: 8px;
  height: 8px;
  border-radius: 3px;
  background: linear-gradient(135deg, #4663ff, #354de3);
  transform: translateY(-50%) scale(0);
  left: -19px;
}

.equipment-pill input[type='checkbox']:checked + .equipment-label::before {
  border-color: rgba(70, 97, 238, 0.9);
  background: rgba(70, 97, 238, 0.1);
  box-shadow: 0 0 0 2px rgba(70, 97, 238, 0.15);
}

.equipment-pill input[type='checkbox']:checked + .equipment-label::after {
  transform: translateY(-50%) scale(1);
}

@media (max-width: 480px) {
  .equipment-group {
    padding: 16px;
  }
  .equipment-pill {
    padding: 10px 16px 10px 42px;
  }
  .equipment-label::before {
    left: -26px;
  }
  .equipment-label::after {
    left: -17px;
  }
}

.repeater {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.qi-subheader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.qi-subheader__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #16285d;
}

.repeater-item {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  padding: 18px;
  border: 1px dashed rgba(76, 108, 255, 0.45);
  border-radius: 16px;
  background: #ffffff;
}

.link {
  background: transparent;
  border: none;
  color: #3452e5;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  transition: color 0.15s ease;
}

.link:hover {
  color: #2537a8;
}

.link.danger {
  color: #d3474c;
}

.pathology-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 16px;
}

.pathology-card {
  border: 1px solid rgba(113, 133, 222, 0.2);
  border-radius: 18px;
  padding: 18px;
  background: rgba(247, 249, 255, 0.85);
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.pathology-card:hover {
  border-color: rgba(76, 108, 255, 0.48);
  box-shadow: 0 16px 32px -28px rgba(18, 26, 70, 0.5);
}

.pathology-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(76, 108, 255, 0.12);
}

.upload-card {
  border: 1px dashed rgba(76, 108, 255, 0.4);
  border-radius: 18px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: rgba(248, 250, 255, 0.95);
}

.upload-card input[type='file'] {
  border: none;
  padding: 0;
}

.data-summary {
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  margin-bottom: 32px;
}

.data-column {
  border: 1px solid rgba(32, 45, 92, 0.14);
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(244, 246, 255, 0.85) 0%, #ffffff 100%);
  box-shadow: inset 0 0 0 1px rgba(42, 61, 133, 0.05);
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.data-header {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.data-header h2 {
  margin: 0;
  font-size: 1.12rem;
  font-weight: 600;
  color: #101938;
}

.data-subtitle {
  margin: 4px 0 0;
  font-size: 0.92rem;
  color: #1b2548;
  font-weight: 500;
}

.data-meta {
  font-size: 0.85rem;
  color: #334070;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.data-search {
  border: 1px solid rgba(47, 68, 148, 0.25);
  border-radius: 14px;
  padding: 10px 14px;
  font-size: 0.95rem;
  color: #101938;
  background: rgba(255, 255, 255, 0.92);
}

.data-search:disabled {
  opacity: 0.6;
}

.data-groups {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.data-group__title {
  margin: 0 0 6px;
  font-size: 0.94rem;
  font-weight: 600;
  color: #16285d;
}

.data-row {
  display: grid;
  grid-template-columns: minmax(160px, 1fr) minmax(0, 2fr);
  gap: 12px;
  align-items: start;
  padding: 12px 14px;
  border: 1px solid rgba(47, 68, 148, 0.18);
  border-radius: 16px;
  background: rgba(247, 249, 255, 0.75);
}

.data-row.multiline {
  align-items: stretch;
}

.data-key {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a2760;
  word-break: break-word;
}

.data-value {
  font-size: 0.9rem;
  color: #101938;
  word-break: break-word;
}

.data-value pre {
  margin: 0;
  font-size: 0.88rem;
  font-family: 'SFMono-Regular', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  background: transparent;
  white-space: pre-wrap;
}

.data-state {
  padding: 14px;
  border: 1px dashed rgba(47, 68, 148, 0.25);
  border-radius: 16px;
  font-size: 0.9rem;
  color: #1a2760;
  text-align: center;
}

.data-state.error {
  color: #b91c1c;
  border-color: rgba(185, 28, 28, 0.35);
  background: rgba(248, 113, 113, 0.08);
}

.toast-stack {
  position: fixed;
  top: 24px;
  right: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 1400;
}

.toast {
  min-width: 240px;
  max-width: 320px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  color: #f8fafc;
  box-shadow: 0 18px 32px rgba(15, 23, 42, 0.24);
  background: #1e293b;
}

.toast--success {
  background: #15803d;
}

.toast--error {
  background: #b91c1c;
}

.toast--info {
  background: #2563eb;
}

.toast-message {
  flex: 1;
  font-size: 0.9rem;
}

.toast-close {
  border: none;
  background: transparent;
  color: inherit;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.2s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 640px) {
  .toast-stack {
    left: 16px;
    right: 16px;
    top: 16px;
  }
  .toast {
    max-width: none;
    width: 100%;
  }
}

.photo-intro {
  margin: 0;
  font-size: 0.95rem;
  color: #121d3a;
  font-weight: 500;
}

.photo-grid {
  display: grid;
  gap: 22px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (max-width: 720px) {
  .photo-grid {
    grid-template-columns: 1fr;
  }
}

.photo-card {
  border: 1px solid rgba(32, 45, 92, 0.14);
  border-radius: 20px;
  padding: 20px 22px;
  background: linear-gradient(180deg, rgba(244, 246, 255, 0.95) 0%, #ffffff 100%);
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: inset 0 0 0 1px rgba(42, 61, 133, 0.05);
}

.photo-card__title {
  margin: 0;
  font-size: 1.02rem;
  font-weight: 600;
  color: #101938;
}

.photo-card__description {
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.45;
  color: #1b2548;
}

.photo-card__upload {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.photo-card__upload-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #101938;
}

.photo-card__upload input[type='file'] {
  border: 1px dashed rgba(47, 68, 148, 0.35);
  border-radius: 14px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.9);
  color: #101938;
}

.photo-card .file-meta {
  margin-top: 4px;
  color: #101938;
}

.photo-card__status {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.upload-progress {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #2b3a69;
}

.spinner {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(43, 58, 105, 0.2);
  border-top-color: #4056d6;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.file-preview {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(64, 86, 214, 0.08);
}

.file-preview img {
  display: block;
  width: 100%;
  object-fit: cover;
}

.file-preview--pdf iframe {
  width: 100%;
  height: 160px;
  border: none;
  background: #ffffff;
}

.idoneita-grid {
  display: grid;
  gap: 18px;
}

@media (min-width: 860px) {
  .idoneita-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.autocert-card {
  border: 1px solid rgba(76, 108, 255, 0.22);
  border-radius: 18px;
  padding: 18px 20px;
  background: rgba(248, 250, 255, 0.9);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.autocert-card h3 {
  margin: 0;
  font-size: 1.05rem;
  color: #16285d;
  font-weight: 600;
}

.autocert-textarea {
  min-height: 160px;
  resize: vertical;
}

.legacy-autocert {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}

.small-text {
  font-size: 0.9rem;
  line-height: 1.5;
}

.certification-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: rgba(76, 108, 255, 0.06);
}

.repeater-item .certification-panel {
  grid-column: 1 / -1;
}

.certification-upload {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.certification-upload input[type='file'] {
  border: none;
  padding: 0;
}

.certification-file-meta {
  justify-content: flex-start;
  gap: 12px;
}

.accordion-question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-radius: 16px;
  background: rgba(76, 108, 255, 0.08);
}

.accordion-question span {
  font-weight: 600;
  color: #16285d;
}

.option-toggle {
  display: inline-flex;
  gap: 12px;
}

.choice-radio {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: 1px solid rgba(76, 108, 255, 0.3);
  border-radius: 999px;
  padding: 6px 16px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
  color: #16285d;
}

.choice-radio input[type='radio'] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.choice-radio span {
  font-weight: 600;
  color: #16285d;
}

.choice-radio--checked {
  background: rgba(76, 108, 255, 0.16);
  color: #16285d;
  border-color: rgba(76, 108, 255, 0.45);
}

.choice-radio--checked span {
  color: #16285d;
}

.accordion-body {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 18px;
}

.file-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.92rem;
}

.file-meta a {
  color: #3452e5;
  font-weight: 600;
  text-decoration: none;
}

.form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding-top: 22px;
  border-top: 1px solid rgba(76, 108, 255, 0.18);
}

.primary-button,
.secondary-button {
  border-radius: 999px;
  padding: 12px 28px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.25s ease, background 0.25s ease;
}

.primary-button {
  background: linear-gradient(135deg, #4663ff, #354de3);
  border: none;
  color: #ffffff;
}

.primary-button--success {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  box-shadow: 0 16px 32px -22px rgba(34, 197, 94, 0.6);
}

.primary-button--success:hover:not(:disabled) {
  box-shadow: 0 18px 36px -18px rgba(22, 163, 74, 0.65);
}

.primary-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 16px 32px -22px rgba(69, 94, 255, 0.65);
}

.secondary-button {
  background: rgba(76, 108, 255, 0.08);
  border: 1px solid rgba(76, 108, 255, 0.36);
  color: #3452e5;
}

.secondary-button:hover:not(:disabled) {
  border-color: rgba(76, 108, 255, 0.6);
  color: #2537a8;
}

.primary-button:disabled,
.secondary-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.dirty-indicator {
  color: #f59e0b;
  font-weight: 600;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fold-enter-active,
.fold-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.fold-enter-from,
.fold-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 1280px) {
  .content-container {
    padding-right: 32px;
    padding-bottom: 56px;
  }
}

@media (max-width: 1024px) {
  .content-container {
    padding-right: 24px;
    padding-bottom: 48px;
  }

  .questionario-content {
    width: 100%;
  }

  .questionario-card {
    padding: 32px clamp(22px, 6vw, 34px);
    border-radius: 24px;
  }

  .qi-block {
    padding: 24px clamp(18px, 4vw, 28px);
  }
}

@media (max-width: 640px) {
  .content-container {
    padding-right: 18px;
    padding-bottom: 40px;
  }

  .questionario-card {
    padding: 28px 22px;
  }

  .card-header {
    align-items: flex-start;
  }

  .meta {
    width: 100%;
    justify-content: flex-start;
  }

  .qi-block__header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
